import { useRef, useState, useCallback, useEffect } from 'react'
import { X, Loader2 } from 'lucide-react'
import { apiClient } from '@/api/client'

export interface Contact {
  id: string
  name: string
  phone: string
  address?: string | null
}

interface ContactAutocompleteProps {
  token: string
  value: string
  onChange: (value: string) => void
  onSelect: (contact: Contact) => void
  placeholder?: string
  className?: string
  type?: 'text' | 'tel'
  /** 已驗證的 PIN；未提供時下拉選單不顯示刪除按鈕（刪除端點需要 PIN） */
  pin?: string
  onDeleted?: (contact: Contact) => void
}

const inputCls = (hasError?: boolean) =>
  `w-full border rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300 bg-white pr-8 ${hasError ? 'border-red-400' : 'border-gray-300'}`

export default function ContactAutocomplete({
  token,
  value,
  onChange,
  onSelect,
  placeholder,
  className,
  type = 'text',
  pin,
  onDeleted,
}: ContactAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchContacts = useCallback((q: string) => {
    setIsSearching(true)
    apiClient.get(`/public/booking/${token}/contacts`, { params: q ? { q } : {} })
      .then(res => setContacts(res.data || []))
      .catch(() => setContacts([]))
      .finally(() => setIsSearching(false))
  }, [token])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
    setIsOpen(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchContacts(e.target.value), 300)
  }

  const handleFocus = () => {
    setIsOpen(true)
    fetchContacts(value)
  }

  const handleSelect = (contact: Contact) => {
    onSelect(contact)
    setIsOpen(false)
  }

  const handleDelete = async (e: React.MouseEvent, contact: Contact) => {
    e.stopPropagation()
    if (!pin) return
    try {
      await apiClient.delete(`/public/booking/${token}/contacts/${contact.id}`, { headers: { 'x-booking-pin': pin } })
      setContacts(prev => prev.filter(c => c.id !== contact.id))
      onDeleted?.(contact)
    } catch {
      // 靜默失敗，聯絡人仍留在下次搜尋結果中
    }
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        type={type}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        autoComplete="off"
        className={className || inputCls()}
      />
      {isOpen && (contacts.length > 0 || isSearching) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-[100]">
          {isSearching && contacts.length === 0 ? (
            <div className="flex items-center justify-center py-3 text-gray-500 text-sm gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              搜尋中...
            </div>
          ) : (
            <ul className="max-h-48 overflow-y-auto py-1">
              {contacts.map(c => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(c)}
                    className="w-full flex items-center justify-between gap-2 text-left px-3 py-2 hover:bg-gray-50 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">{c.name}　{c.phone}</div>
                      {c.address && <div className="text-xs text-gray-500 truncate">{c.address}</div>}
                    </div>
                    {pin && (
                      <span
                        role="button"
                        onClick={(e) => handleDelete(e, c)}
                        className="shrink-0 text-gray-300 hover:text-red-400 transition-colors p-1"
                      >
                        <X className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
